/**
 * FullObjectPanelInternal Component
 *
 * Internal panel component displaying full JSON object with copy-to-clipboard.
 * Extracted from EngineOutputInternalSection for reuse in tab-based layouts.
 * This is an internal/debug component (not user-facing).
 */

import { useState, useCallback } from "react";
import { ClipboardDocumentIcon, CheckIcon } from "@heroicons/react/20/solid";
import { Text, Subheading } from "@a7y/tailwind-catalyst";
import { DevCardBadge } from "./DevCardBadge";

export interface FullObjectPanelInternalProps {
  /** Data object to display as JSON */
  data: unknown;
}

export function FullObjectPanelInternal({ data }: FullObjectPanelInternalProps) {
  const [copied, setCopied] = useState(false);

  const hasData = data !== null && data !== undefined;
  const jsonString = hasData ? JSON.stringify(data, null, 2) : null;

  const handleCopy = useCallback(async () => {
    if (!jsonString) return;

    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy to clipboard:", err);
    }
  }, [jsonString]);

  return (
    <DevCardBadge name="FullObjectPanelInternal">
    <div className="border border-gray-200 dark:border-zinc-700 rounded-lg overflow-hidden">
      <div className="bg-gray-100 dark:bg-zinc-800 px-4 py-2 border-b border-gray-200 dark:border-zinc-700 flex items-center justify-between">
        <Subheading level={4}>Full Object</Subheading>
        {hasData && (
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors"
            title="Copy JSON to clipboard"
          >
            {copied ? (
              <>
                <CheckIcon className="size-4 text-success" />
                <span className="text-success">Copied!</span>
              </>
            ) : (
              <>
                <ClipboardDocumentIcon className="size-4" />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </div>
      {hasData ? (
        <pre
          className="text-xs bg-gray-900 dark:bg-zinc-950 p-4 overflow-auto text-gray-100 dark:text-gray-200 font-mono"
          style={{ maxHeight: "500px" }}
        >
          <code>{jsonString}</code>
        </pre>
      ) : (
        <div className="p-4">
          <Text className="text-gray-500 dark:text-gray-400 italic">No data available</Text>
        </div>
      )}
    </div>
    </DevCardBadge>
  );
}
